import type { ZodTypeAny } from 'zod';

/** Execution context handed to every tool. */
export interface ToolContext {
  /** Absolute repository root; all file paths are confined to it. */
  readonly repoRoot: string;
}

/**
 * The permission type a mutating tool call resolves to. Each kind carries its
 * own independent consent state in the CLI's permission store:
 *  - `edit`   — modifying an existing file
 *  - `create` — creating a new file/resource
 *  - `delete` — removing a file
 *  - `run`    — executing a shell/system command
 */
export type ActionKind = 'edit' | 'delete' | 'create' | 'run';

/** All permission kinds, for iteration/validation (order = display order). */
export const ACTION_KINDS: readonly ActionKind[] = ['edit', 'create', 'delete', 'run'];

/** The outcome of running a tool — `output` is fed back to the model verbatim. */
export interface ToolResult {
  readonly ok: boolean;
  readonly output: string;
}

/**
 * A tool the agent can call. `parameters` is the JSON Schema sent to the model;
 * `schema` is the Zod schema used to validate the model's arguments before
 * execution (model tool-call args are untrusted). `mutating` tools are gated
 * behind a permission prompt.
 */
export interface ToolSpec {
  readonly name: string;
  readonly description: string;
  readonly parameters: Record<string, unknown>;
  readonly mutating: boolean;
  readonly schema: ZodTypeAny;
  /** A short, human-readable preview of what executing `args` will do (permission gate). */
  preview(args: unknown, ctx: ToolContext): string;
  /**
   * Resolve which permission {@link ActionKind} executing `args` needs, or null
   * for a read-only call (never gated). May inspect the filesystem — e.g.
   * write_file is `edit` for an existing file and `create` for a new one.
   * Invariant: returns null iff `mutating` is false.
   */
  classify(args: unknown, ctx: ToolContext): ActionKind | null;
  run(args: unknown, ctx: ToolContext): Promise<ToolResult>;
}
